// @ts-strict-ignore
import Layout, { Content } from 'antd/lib/layout/layout';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compareVersions, isReleaseSkipped } from '../helpers';
import type { RootState } from '../reducers/reducers';
import type { AppComponentProps, AppRouteComponentMatchParams, Nullable } from '../typescriptHelpers';
import { antGrey, runwayColors, titleStyle } from './styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button/button';
import { Event } from '../utils/analytics';
import { MehOutlined } from '@ant-design/icons';
import Result from 'antd/lib/result';
import { LiveReleaseComponent } from './appHome/LiveReleaseComponent';
import { NextReleaseComponent } from './appHome/NextReleaseComponent';
import { UpcomingReleaseComponent } from './appHome/UpcomingReleaseComponent';
import { PastReleaseComponent } from './appHome/PastReleaseComponent';
import Spin from 'antd/lib/spin';
import { trackAppRouteEvent } from '../actions/trackEvent';
import {
    selectApp,
    selectAppPlatform,
    selectAppStoreIntegration,
    selectAppStoreType,
    selectIsAppsDemoModeEnabled,
    selectWorkflowEnabledSteps,
    selectStabilityIntegrations,
    selectShouldHideNonBlockingSteps,
    selectReleaseStepsBlockingConfigForReleaseType,
    selectOrg,
    selectUser,
    selectAllAppsIdsInOrg,
    selectAllAppGroupNamesInOrg,
    selectDemoApp,
} from '../selectors/selectors';
import { selectDestinationsData, selectShouldRenderWorkflowsOverviewForApp } from '../selectors/summaryViewSelectors';
import { MobileSideBarHeader } from './buildDistribution/mobileComponents/MobileSideBarHeader';
import { SkippedReleaseComponent } from './appHome/SkippedReleaseComponent';
import { QualitativeOnboardingTaskList } from '@/components/QualitativeOnboardingTaskList';
import { useIsLoading } from '@/utils/hooks/helpers';
import { globalMessage } from '@/utils/globalMessage';
import { useGetWorkflowOverviewQuery } from '@/middlewares/defaultApi';
import { AllLifecycleSteps, AppPlatform, AppStoreType, Integration, LifecycleStep, ReleaseStepType } from '@/types';
import { OrgDestinationsResponse, PageViewType } from '@/types/summaryView';
import { useParams } from 'react-router-dom';
import { useSummaryView } from '@/utils/hooks/useSummaryView';
import { useDestinationSelection, getOptimalDestinationQueryParams } from '@/utils/hooks/useDestinationSelection';
import { OverviewTableHeader, PageViewContext } from './orgSummary/OverviewTableHeader';
import { createFilterApplyTracker, getDestinationDisplayText } from '@/helpers/summaryViewHelpers';
import { SummaryTable } from './orgSummary/SummaryTable';
import { WorkflowOverview } from './workflowOverview/WorkflowOverview';
import { DestinationSelector } from '@/components/appHome/DestinationSelector';
import { Destination } from '@/types/destinations';
import { useGetAppHomeQuery } from '@/middlewares/defaultApi';
import { FetchableEntityType } from '@/entityType';
import { tryFetchEntity } from '@/actions/entities';
import Flex from 'antd/lib/flex';

export const AppHomeComponent: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const { orgId, appId } = props;
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const workflowId = useSelector((state: RootState) => {
        const app = selectApp(state, appId);
        return app.workflowId;
    });

    const dispatch = useDispatch();

    // Shared org/app data
    const destinationsAndVersions = useSelector((state: RootState) => selectDestinationsData(state, orgId));
    const isLoadingDestinationsAndVersions = useIsLoading(FetchableEntityType.OrgDestinationsAndVersions, orgId);

    React.useEffect(() => {
        if (destinationsAndVersions) return;
        dispatch(tryFetchEntity(FetchableEntityType.OrgDestinationsAndVersions, { orgId }, orgId));
    }, [dispatch, orgId, destinationsAndVersions]);

    // Determines whether to render workflows overview (based on app migration/SRR checks)
    const shouldRenderWorkflowsOverview = useSelector((state: RootState) =>
        selectShouldRenderWorkflowsOverviewForApp(state, orgId, appId),
    );

    const canAccessTabularView = destinationsAndVersions?.canAccessTabularView ?? false;

    const availableDestinationsForApp: Destination[] = React.useMemo(() => {
        const appEntry = destinationsAndVersions?.appDestinations?.find((app) => app.appId === appId);
        return (
            appEntry?.destinations?.map((d) => ({
                stepConfigId: d.stepConfigId,
                name: d.name,
                platform: d.platform as AppPlatform,
                appStoreType: d.appStoreType as AppStoreType,
                iconURL: d.iconURL,
            })) ?? []
        );
    }, [destinationsAndVersions, appId]);

    // Track pageView changes for amplitude
    const [trackedPageView, setTrackedPageView] = React.useState<string | null>(null);
    const handlePageViewTracking = React.useCallback((pageView: string) => {
        setTrackedPageView((prev) => (prev === pageView ? prev : pageView));
    }, []);

    React.useEffect(() => {
        if (!shouldRenderWorkflowsOverview) {
            handlePageViewTracking(PageViewType.Default);
        }
    }, [shouldRenderWorkflowsOverview, handlePageViewTracking]);

    React.useEffect(() => {
        if (!trackedPageView) return;
        dispatch(
            trackAppRouteEvent({
                orgId,
                appId,
                eventName: Event.ViewAppHome,
                viewType: trackedPageView,
            }),
        );
    }, [dispatch, orgId, appId, trackedPageView]);

    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    // Workflow overview state
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));
    const shouldHideNonBlockingSteps = useSelector((state: RootState) =>
        selectShouldHideNonBlockingSteps(state, appId),
    );
    const nonBlockingStepsConfigMap = useSelector((state: RootState) =>
        selectReleaseStepsBlockingConfigForReleaseType(state, appId),
    );

    const workflowEnabledLifecycleSteps = AllLifecycleSteps.filter((lifecycleStep) => {
        switch (lifecycleStep) {
            case LifecycleStep.Kickoff:
                return !!workflowEnabledSteps[ReleaseStepType.Kickoff];
            case LifecycleStep.Submission:
                return !!workflowEnabledSteps[ReleaseStepType.Submission];
            case LifecycleStep.Release:
                return (
                    !!workflowEnabledSteps[ReleaseStepType.Takeoff] ||
                    !!workflowEnabledSteps[ReleaseStepType.CIDistribution]
                );
            default:
                return false;
        }
    });

    // Legacy App Home data (using RTK Query for speed)
    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const stabilityIntegrations = useSelector((state: RootState) => selectStabilityIntegrations(state, appId));
    const stabilityIntegration = stabilityIntegrations[0];
    const appStoreIntegration = useSelector((state: RootState) => selectAppStoreIntegration(state, appId));

    const { data, isFetching, error, refetch } = useGetAppHomeQuery(
        { orgId, appId },
        { skip: !destinationsAndVersions || shouldRenderWorkflowsOverview || isViewingAppsInDemoMode },
    );

    function sortReleasesByVersion<T>(releases: Nullable<T & { version: string }>[]): T[] {
        return (
            releases?.slice()?.sort((releaseA, releaseB) => compareVersions(releaseA.version, releaseB.version)) ?? []
        );
    }
    const demoApp = useSelector((state: RootState) => selectDemoApp(state, appId));
    const appHome = isViewingAppsInDemoMode ? demoApp?.appHome : data?.data;
    const liveRelease = appHome?.liveRelease;
    const nextRelease = appHome?.nextRelease;
    const upcomingReleases = sortReleasesByVersion(appHome?.upcomingReleases);
    const pastReleases = sortReleasesByVersion(appHome?.pastReleases);

    const noReleases =
        !isFetching &&
        !liveRelease &&
        !nextRelease &&
        (!upcomingReleases || upcomingReleases?.length === 0) &&
        (!pastReleases || pastReleases?.length === 0);

    if (isLoadingDestinationsAndVersions) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                }}
            >
                <Spin size="large" />
                <Text style={{ color: runwayColors.primary, paddingTop: 10 }}>Loading...</Text>
            </div>
        );
    }

    return (
        <MobileSideBarHeader orgId={orgId} appId={appId} title="App Home">
            <Layout
                style={{
                    overflowY: 'auto',
                    padding: 36,
                    minWidth: 980,
                }}
            >
                {/*
            Leaving this here until we can better support app-level demo mode
            <Alert
                type="info"
                icon={null}
                message="Not ready to start onboarding?"
                description={
                    <Flex vertical gap={4}>
                        <Text>
                            Try Runway in demo mode to see how the dashboard will look once setup is complete.
                        </Text>
                        <Text type="secondary">
                            Demo mode is read-only — all actions on the dashboard will be disabled.
                        </Text>
                    </Flex>
                }
                action={<TrackedButton onClick={viewInDemoMode}>🔮 Try it in demo mode 🔮</TrackedButton>}
                style={{
                    borderStyle: 'solid',
                    borderColor: hexStringToRGBAString(runwayColors.primary, 0.8),
                    borderWidth: 1,
                    marginBottom: 28,
                }}
            ></Alert> */}

                <QualitativeOnboardingTaskList orgId={props.orgId} appId={props.appId} />
                <Content
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {canAccessTabularView ?
                        <TabularViewContent
                            orgId={orgId}
                            appId={appId}
                            orgIdParam={orgIdParam}
                            appIdParam={appIdParam}
                            workflowId={workflowId}
                            destinationsAndVersions={destinationsAndVersions}
                            stabilityIntegrations={stabilityIntegrations}
                            workflowEnabledSteps={workflowEnabledSteps}
                            workflowEnabledLifecycleSteps={workflowEnabledLifecycleSteps}
                            shouldHideNonBlockingSteps={shouldHideNonBlockingSteps}
                            nonBlockingStepsConfigMap={nonBlockingStepsConfigMap}
                            shouldRenderWorkflowsOverview={shouldRenderWorkflowsOverview}
                            onPageViewChange={handlePageViewTracking}
                            legacyProps={{
                                upcomingReleases,
                                nextRelease,
                                liveRelease,
                                pastReleases,
                                noReleases,
                                isFetching,
                                error,
                                refetch,
                                platform,
                                appStoreType,
                                stabilityIntegration,
                                appStoreIntegration,
                            }}
                        />
                    : shouldRenderWorkflowsOverview ?
                        <WorkflowOverviewContent
                            orgId={orgId}
                            appId={appId}
                            orgIdParam={orgIdParam}
                            appIdParam={appIdParam}
                            workflowId={workflowId}
                            availableDestinations={availableDestinationsForApp}
                            stabilityIntegrations={stabilityIntegrations}
                            workflowEnabledSteps={workflowEnabledSteps}
                            workflowEnabledLifecycleSteps={workflowEnabledLifecycleSteps}
                            shouldHideNonBlockingSteps={shouldHideNonBlockingSteps}
                            nonBlockingStepsConfigMap={nonBlockingStepsConfigMap}
                            onPageViewChange={handlePageViewTracking}
                        />
                    :   <LegacyAppHomeContent
                            orgId={orgId}
                            appId={appId}
                            upcomingReleases={upcomingReleases}
                            nextRelease={nextRelease}
                            liveRelease={liveRelease}
                            pastReleases={pastReleases}
                            noReleases={noReleases}
                            isFetching={isFetching}
                            error={error}
                            refetch={refetch}
                            platform={platform}
                            appStoreType={appStoreType}
                            stabilityIntegration={stabilityIntegration}
                            appStoreIntegration={appStoreIntegration}
                            showTitle
                        />
                    }
                </Content>
            </Layout>
        </MobileSideBarHeader>
    );
};

interface LegacyAppHomeContentProps {
    orgId: string;
    appId: string;
    upcomingReleases?: any[];
    nextRelease?: any;
    liveRelease?: any;
    pastReleases?: any[];
    noReleases?: boolean;
    isFetching?: boolean;
    error?: any;
    refetch?: () => void;
    platform?: AppPlatform;
    appStoreType?: AppStoreType;
    stabilityIntegration?: Integration;
    appStoreIntegration?: Integration;
    showTitle?: boolean;
}

const LegacyAppHomeContent: React.FC<LegacyAppHomeContentProps> = ({
    orgId,
    appId,
    upcomingReleases,
    nextRelease,
    liveRelease,
    pastReleases,
    noReleases,
    isFetching,
    error,
    refetch,
    platform,
    appStoreType,
    stabilityIntegration,
    appStoreIntegration,
    showTitle = false,
}) => {
    return (
        <>
            {showTitle && <Text style={{ ...titleStyle, marginBottom: 24 }}>Overview</Text>}
            <Flex
                vertical
                justify="center"
                flex={'1'}
                style={{
                    width: '100%',
                    paddingBottom: 65,
                    ...(!showTitle && { paddingTop: 24 }),
                }}
            >
                {upcomingReleases?.map((upcomingReleaseSummary) => (
                    <UpcomingReleaseComponent
                        key={upcomingReleaseSummary.id}
                        summary={upcomingReleaseSummary}
                        orgId={orgId}
                        appId={appId}
                        appStoreType={appStoreType}
                        platform={platform}
                    />
                ))}
                {nextRelease && (
                    <NextReleaseComponent
                        key={nextRelease.id}
                        summary={nextRelease}
                        orgId={orgId}
                        appId={appId}
                        platform={platform}
                        appStoreType={appStoreType}
                        stabilityIntegration={stabilityIntegration}
                        appStoreIntegration={appStoreIntegration}
                    />
                )}
                {liveRelease && (
                    <LiveReleaseComponent
                        key={liveRelease.id}
                        summary={liveRelease}
                        orgId={orgId}
                        appId={appId}
                        platform={platform}
                        appStoreType={appStoreType}
                        stabilityIntegration={stabilityIntegration}
                        appStoreIntegration={appStoreIntegration}
                    />
                )}
                {pastReleases?.map((pastReleaseSummary) =>
                    isReleaseSkipped(pastReleaseSummary.status) ?
                        <SkippedReleaseComponent
                            key={pastReleaseSummary.id}
                            summary={pastReleaseSummary}
                            orgId={orgId}
                            appId={appId}
                        />
                    :   <PastReleaseComponent
                            key={pastReleaseSummary.id}
                            summary={pastReleaseSummary}
                            orgId={orgId}
                            appId={appId}
                            platform={platform}
                            appStoreType={appStoreType}
                            stabilityIntegration={stabilityIntegration}
                            appStoreIntegration={appStoreIntegration}
                        />,
                )}
                {noReleases && (
                    <Text strong type="secondary">
                        No releases
                    </Text>
                )}
                {isFetching && <Spin />}
                {error && (
                    <Result
                        icon={<MehOutlined style={{ color: antGrey.grey5 }} />}
                        subTitle="Error fetching app overview"
                        extra={<Button onClick={refetch}>Try again</Button>}
                    />
                )}
            </Flex>
        </>
    );
};

interface TabularViewContentProps {
    orgId: string;
    appId: string;
    orgIdParam: string;
    appIdParam: string;
    workflowId: string;
    destinationsAndVersions: OrgDestinationsResponse;
    stabilityIntegrations: Integration[];
    workflowEnabledSteps: ReturnType<typeof selectWorkflowEnabledSteps>;
    workflowEnabledLifecycleSteps: LifecycleStep[];
    shouldHideNonBlockingSteps: boolean;
    nonBlockingStepsConfigMap: ReturnType<typeof selectReleaseStepsBlockingConfigForReleaseType>;
    shouldRenderWorkflowsOverview: boolean;
    onPageViewChange: (pageView: string) => void;
    legacyProps: Omit<LegacyAppHomeContentProps, 'orgId' | 'appId' | 'showTitle'>;
}

const TabularViewContent: React.FC<TabularViewContentProps> = ({
    orgId,
    appId,
    orgIdParam,
    appIdParam,
    workflowId,
    destinationsAndVersions,
    stabilityIntegrations,
    workflowEnabledSteps,
    workflowEnabledLifecycleSteps,
    shouldHideNonBlockingSteps,
    nonBlockingStepsConfigMap,
    shouldRenderWorkflowsOverview,
    onPageViewChange,
    legacyProps,
}) => {
    const summary = useSummaryView({ orgId, appId });
    const {
        preferences,
        availableDestinations,
        initializeWithDefaults,
        pagination: { destinationLimit },
    } = summary;

    const selectedDestinationIds = preferences.selectedDestinationIds;

    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const app = useSelector((state: RootState) => selectApp(state, appId));
    const user = useSelector((state: RootState) => selectUser(state));
    const appsInOrg = useSelector((state: RootState) => selectAllAppsIdsInOrg(state, orgId));
    const appGroupsInOrg = useSelector((state: RootState) => selectAllAppGroupNamesInOrg(state, orgId));

    const handleTrackFilterApply = React.useMemo(
        () => createFilterApplyTracker(org, user, appsInOrg, appGroupsInOrg, preferences, PageViewContext.AppHome, app),
        [org, app, user, appsInOrg, appGroupsInOrg, preferences],
    );

    // Track page view changes
    React.useEffect(() => {
        const { pageView, tabularLayout } = preferences;
        const viewType = pageView === PageViewType.Default ? PageViewType.Default : `${pageView}-${tabularLayout}`;
        onPageViewChange(viewType);
    }, [onPageViewChange, preferences.pageView, preferences.tabularLayout]);

    const appEntry = React.useMemo(() => {
        if (!destinationsAndVersions) return undefined;
        return destinationsAndVersions.appDestinations?.find((app) => app.appId === appId);
    }, [destinationsAndVersions, appId]);

    const workflowDestinationIds = React.useMemo(() => {
        if (!destinationsAndVersions) return [];
        return appEntry?.destinations?.map((d) => d.stepConfigId) ?? destinationsAndVersions.allDestinationIds ?? [];
    }, [destinationsAndVersions, appEntry]);

    const workflowVersions = React.useMemo(() => {
        if (!destinationsAndVersions) return [];
        return appEntry?.versions ?? destinationsAndVersions.allVersions ?? [];
    }, [destinationsAndVersions, appEntry]);

    const initialDestinationIds = React.useMemo(() => {
        if (workflowDestinationIds.length === 0) return [];
        return destinationLimit ? workflowDestinationIds.slice(0, destinationLimit) : workflowDestinationIds;
    }, [workflowDestinationIds, destinationLimit]);

    const defaultVersionsForAppHome = React.useMemo(() => {
        if (appEntry?.activeVersion) {
            return [appEntry.activeVersion];
        }
        if (workflowVersions.length > 0) {
            return [workflowVersions[0]];
        }
        return [];
    }, [appEntry, workflowVersions]);

    React.useEffect(() => {
        if (!destinationsAndVersions) return;
        if (!destinationLimit) return;
        if (initialDestinationIds.length === 0 || workflowVersions.length === 0) return;

        initializeWithDefaults({
            versions: defaultVersionsForAppHome,
            destinationIds: initialDestinationIds,
        });
    }, [
        destinationsAndVersions,
        destinationLimit,
        initialDestinationIds,
        workflowVersions.length,
        defaultVersionsForAppHome,
        initializeWithDefaults,
    ]);

    const destinationIds = React.useMemo(
        () => (workflowDestinationIds.length > 0 ? workflowDestinationIds : selectedDestinationIds),
        [workflowDestinationIds, selectedDestinationIds],
    );

    const workflowOverviewQueryParams = React.useMemo(
        () => getOptimalDestinationQueryParams(destinationIds, workflowDestinationIds),
        [destinationIds, workflowDestinationIds],
    );

    const {
        data: workflowOverviewData,
        isLoading: getWorkflowOverviewIsLoading,
        refetch: refetchWorkflowOverview,
    } = useGetWorkflowOverviewQuery(
        { orgId, workflowId, ...workflowOverviewQueryParams },
        {
            skip:
                !shouldRenderWorkflowsOverview ||
                !workflowId ||
                !destinationsAndVersions ||
                destinationIds.length === 0,
        },
    );

    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const demoApp = useSelector((state: RootState) => selectDemoApp(state, appId));
    const workflowOverview = isViewingAppsInDemoMode ? demoApp?.workflowOverview : workflowOverviewData?.data;

    React.useEffect(() => {
        if (workflowOverviewData?.errorMessage) {
            globalMessage.error(workflowOverviewData?.errorMessage);
        }
    }, [workflowOverviewData?.errorMessage]);

    const retryWorkflowOverviewFetch = async () => {
        const res = await refetchWorkflowOverview();
        if (res.data?.errorMessage) {
            globalMessage.error(res.data?.errorMessage);
        }
    };

    const isWorkflowsEnabledAndMultiDestination = availableDestinations.length > 1;
    const runCardDestinationDisplayText = React.useMemo(
        () => getDestinationDisplayText(selectedDestinationIds, availableDestinations),
        [selectedDestinationIds, availableDestinations],
    );

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
            <OverviewTableHeader
                context={PageViewContext.AppHome}
                summary={summary}
                onTrackFilterApply={handleTrackFilterApply}
            />
            {preferences.pageView === PageViewType.Tabular ?
                <div style={{ overflow: 'hidden', flex: 1, height: '100%', minHeight: 0, paddingBottom: 40 }}>
                    <SummaryTable summary={summary} />
                </div>
            : shouldRenderWorkflowsOverview ?
                <WorkflowOverview
                    workflowOverview={workflowOverview}
                    isLoading={!!getWorkflowOverviewIsLoading}
                    shouldDisplayFetchFailed={!!workflowOverviewData?.errorMessage}
                    onRetry={retryWorkflowOverviewFetch}
                    orgId={orgId}
                    appId={appId}
                    orgIdParam={orgIdParam}
                    appIdParam={appIdParam}
                    selectedDestinationIds={selectedDestinationIds}
                    resolvedDestinations={availableDestinations}
                    runCardDestinationDisplayText={runCardDestinationDisplayText}
                    isWorkflowsEnabledAndMultiDestination={isWorkflowsEnabledAndMultiDestination}
                    stabilityIntegrations={stabilityIntegrations}
                    workflowEnabledSteps={workflowEnabledSteps}
                    workflowEnabledLifecycleSteps={workflowEnabledLifecycleSteps}
                    shouldHideNonBlockingSteps={shouldHideNonBlockingSteps}
                    nonBlockingStepsConfigMap={nonBlockingStepsConfigMap}
                />
            :   <LegacyAppHomeContent orgId={orgId} appId={appId} {...legacyProps} />}
        </div>
    );
};

interface WorkflowOverviewContentProps {
    orgId: string;
    appId: string;
    orgIdParam: string;
    appIdParam: string;
    workflowId: string;
    availableDestinations: Destination[];
    stabilityIntegrations: Integration[];
    workflowEnabledSteps: ReturnType<typeof selectWorkflowEnabledSteps>;
    workflowEnabledLifecycleSteps: LifecycleStep[];
    shouldHideNonBlockingSteps: boolean;
    nonBlockingStepsConfigMap: ReturnType<typeof selectReleaseStepsBlockingConfigForReleaseType>;
    onPageViewChange: (pageView: string) => void;
}

const WorkflowOverviewContent: React.FC<WorkflowOverviewContentProps> = ({
    orgId,
    appId,
    orgIdParam,
    appIdParam,
    workflowId,
    availableDestinations,
    stabilityIntegrations,
    workflowEnabledSteps,
    workflowEnabledLifecycleSteps,
    shouldHideNonBlockingSteps,
    nonBlockingStepsConfigMap,
    onPageViewChange,
}) => {
    const availableDestinationIds = React.useMemo(
        () => availableDestinations.map((d) => d.stepConfigId),
        [availableDestinations],
    );

    const { selectedDestinationIds, setSelectedDestinationIds } = useDestinationSelection({
        appId,
        availableDestinationIds,
    });

    React.useEffect(() => {
        onPageViewChange(PageViewType.Default);
    }, [onPageViewChange]);

    const overviewQueryParams = React.useMemo(
        () => getOptimalDestinationQueryParams(selectedDestinationIds, availableDestinationIds),
        [selectedDestinationIds, availableDestinationIds],
    );

    const {
        data: workflowOverviewData,
        isLoading: getWorkflowOverviewIsLoading,
        refetch: refetchWorkflowOverview,
    } = useGetWorkflowOverviewQuery(
        { orgId, workflowId, ...overviewQueryParams },
        { skip: !workflowId || selectedDestinationIds.length === 0 },
    );

    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const demoApp = useSelector((state: RootState) => selectDemoApp(state, appId));
    const workflowOverview = isViewingAppsInDemoMode ? demoApp?.workflowOverview : workflowOverviewData?.data;

    React.useEffect(() => {
        if (workflowOverviewData?.errorMessage) {
            globalMessage.error(workflowOverviewData?.errorMessage);
        }
    }, [workflowOverviewData?.errorMessage]);

    const retryWorkflowOverviewFetch = async () => {
        const res = await refetchWorkflowOverview();
        if (res.data?.errorMessage) {
            globalMessage.error(res.data?.errorMessage);
        }
    };

    const isWorkflowsEnabledAndMultiDestination = availableDestinations.length > 1;
    const runCardDestinationDisplayText = React.useMemo(
        () => getDestinationDisplayText(selectedDestinationIds, availableDestinations),
        [selectedDestinationIds, availableDestinations],
    );

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            {isWorkflowsEnabledAndMultiDestination && (
                <div style={{ marginBottom: 24, display: 'flex', minWidth: 480 }}>
                    <DestinationSelector
                        destinations={availableDestinations}
                        selectedDestinationIds={selectedDestinationIds}
                        onSelectionChange={setSelectedDestinationIds}
                        requiresConfirmation
                    />
                </div>
            )}
            <div style={{ overflow: 'auto', flex: 1, minHeight: '100%', paddingBottom: 40 }}>
                <WorkflowOverview
                    workflowOverview={workflowOverview}
                    isLoading={!!getWorkflowOverviewIsLoading}
                    shouldDisplayFetchFailed={!!workflowOverviewData?.errorMessage}
                    onRetry={retryWorkflowOverviewFetch}
                    orgId={orgId}
                    appId={appId}
                    orgIdParam={orgIdParam}
                    appIdParam={appIdParam}
                    selectedDestinationIds={selectedDestinationIds}
                    resolvedDestinations={availableDestinations}
                    runCardDestinationDisplayText={runCardDestinationDisplayText}
                    isWorkflowsEnabledAndMultiDestination={isWorkflowsEnabledAndMultiDestination}
                    stabilityIntegrations={stabilityIntegrations}
                    workflowEnabledSteps={workflowEnabledSteps}
                    workflowEnabledLifecycleSteps={workflowEnabledLifecycleSteps}
                    shouldHideNonBlockingSteps={shouldHideNonBlockingSteps}
                    nonBlockingStepsConfigMap={nonBlockingStepsConfigMap}
                />
            </div>
        </div>
    );
};
